/**
 * The whole medical file, as the hospital's staff read it.
 *
 * One component for the doctor before the consultation, the nurse at the bed,
 * the clinics manager printing a file and the secretary keeping it current.
 * Everything is read from GET /api/patients/:id/file, which assembles it from
 * the requests themselves — so a lab result appears here the moment the lab
 * completes it, with nothing to copy and nothing to go stale.
 *
 * Money is shown only when the server sent it; this component never decides
 * who may see a price.
 */

import { forwardRef, useEffect, useRef, useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Field, Icon, Modal, Select, Spinner, Tabs, Textarea } from '../../components/common';
import { patientsApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { PERMISSIONS } from '../../domain/rbac';
import { RequestCard, RequestResult } from './RequestCard';
import { ASSIGNMENT_META, DEPARTMENT_META, ageFrom, formatDate, formatDateTime, formatMoney, printArea } from './labels';
import { formatDmy, ltr } from '../../domain/bidi';
import { TIMELINE_FILTERS, TIMELINE_KINDS, buildTimeline, filterTimeline } from './timeline';
import { AttachmentGrid } from './attachments/Attachments';

export const NOTE_KIND_META = Object.freeze({
  initial_diagnosis: { label: 'تشخيص مبدئي', className: 'border-sky-500/30 bg-sky-500/10 text-sky-200' },
  final_diagnosis: { label: 'تشخيص نهائي', className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-100' },
  complaint: { label: 'شكوى', className: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
  note: { label: 'ملاحظة', className: 'border-white/10 bg-white/5 text-slate-300' },
  ai_summary: { label: 'ملخص الذكاء الاصطناعي', className: 'border-purple-500/30 bg-purple-500/10 text-purple-200' },
});

const GENDER = { male: 'ذكر', female: 'أنثى' };

const VITAL_LABELS = [
  ['bp', 'الضغط', ''],
  ['pulse', 'النبض', '/د'],
  ['temperature', 'الحرارة', '°'],
  ['spo2', 'الأكسجين', '%'],
  ['respiratoryRate', 'التنفس', '/د'],
  ['glucose', 'السكر', ''],
];

export function vitalsText(payload = {}) {
  return VITAL_LABELS.filter(([key]) => payload[key] !== null && payload[key] !== undefined)
    .map(([key, label, unit]) => `${label} ${payload[key]}${unit}`)
    .join(' · ');
}

function Section({ title, icon, children }) {
  return (
    <section className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
        <Icon name={icon} size={16} className="text-cyan-400" /> {title}
      </h4>
      {children}
    </section>
  );
}

/** The white sheet the file prints as. */
export const MedicalFileSheet = forwardRef(function MedicalFileSheet({ file, institutionName }, ref) {
  const p = file.patient;
  const age = ageFrom(p.dateOfBirth);
  const results = file.requests.filter((r) => r.status === 'completed' && r.result);
  return (
    <div ref={ref} className="print-sheet" dir="rtl">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-300 pb-2">
        <div>
          <h2 className="text-base font-bold">{institutionName || 'المنشأة الطبية'}</h2>
          <h1 className="text-xl font-bold">الملف الطبي — {p.fullName}</h1>
        </div>
        <p className="muted text-xs">طُبع في {formatDateTime(new Date())}</p>
      </div>

      <table className="mt-3">
        <tbody>
          <tr>
            <th style={{ width: '22%' }}>السن / النوع</th>
            <td>
              {age !== null ? `${age} سنة` : '—'} {p.gender ? `· ${GENDER[p.gender] || p.gender}` : ''}
              {p.dateOfBirth ? ` · مواليد ${ltr(formatDmy(p.dateOfBirth))}` : ''}
            </td>
            <th style={{ width: '22%' }}>نوع المريض</th>
            <td>
              {p.patientType === 'insured' ? `تأمين — ${p.insuranceProvider || ''}` : 'نقدي'}
              {file.contractParty ? ` · جهة التعاقد: ${file.contractParty.name}` : ''}
            </td>
          </tr>
          <tr>
            <th>الرقم القومي</th>
            <td dir="ltr">{p.nationalId || '—'}</td>
            <th>الهاتف</th>
            <td dir="ltr">{[p.phonePrimary, p.phoneSecondary].filter(Boolean).join(' / ') || '—'}</td>
          </tr>
          <tr>
            <th>الأمراض المزمنة</th>
            <td colSpan={3}>{(p.chronicDiseases || []).join('، ') || '—'}</td>
          </tr>
          <tr>
            <th>الشكوى</th>
            <td colSpan={3}>{p.complaints || '—'}</td>
          </tr>
          <tr>
            <th>التاريخ المرضي</th>
            <td colSpan={3}>{p.medicalHistory || '—'}</td>
          </tr>
        </tbody>
      </table>

      {(p.currentMedications || []).length > 0 && (
        <>
          <h3 className="mt-3 font-bold">الأدوية الحالية</h3>
          <table>
            <thead>
              <tr>
                <th>الدواء</th>
                <th>التركيز</th>
                <th>الجرعة</th>
                <th>المدة</th>
              </tr>
            </thead>
            <tbody>
              {p.currentMedications.map((m, i) => (
                <tr key={i}>
                  <td>{m.name}</td>
                  <td>{m.concentration || '—'}</td>
                  <td>{m.frequency || '—'}</td>
                  <td>{m.duration || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {file.notes.length > 0 && (
        <>
          <h3 className="mt-3 font-bold">التشخيصات والملاحظات</h3>
          <table>
            <tbody>
              {file.notes
                .filter((n) => n.kind !== 'ai_summary')
                .map((n) => (
                  <tr key={n.id}>
                    <th style={{ width: '22%' }}>
                      {NOTE_KIND_META[n.kind]?.label || n.kind}
                      <div className="muted text-[10px]">{formatDate(n.createdAt)}</div>
                    </th>
                    <td className="whitespace-pre-line">{n.text}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}

      {results.length > 0 && (
        <>
          <h3 className="mt-3 font-bold">النتائج</h3>
          <table>
            <tbody>
              {results.map((r) => (
                <tr key={r.id}>
                  <th style={{ width: '22%' }}>
                    {DEPARTMENT_META[r.department]?.label} — {r.title}
                    <div className="muted text-[10px]">{formatDate(r.completedAt)}</div>
                  </th>
                  <td>
                    {r.result.summary || r.result.impression || ''}
                    {Array.isArray(r.result.values) &&
                      r.result.values.map((v, i) => (
                        <div key={i}>
                          {v.test}: {v.value} {v.unit} {v.range ? `(${v.range})` : ''}
                        </div>
                      ))}
                    {r.result.report && <div className="whitespace-pre-line">{r.result.report}</div>}
                    {Array.isArray(r.result.dispensed) &&
                      r.result.dispensed.map((d, i) => (
                        <div key={i}>
                          {d.name} {d.quantity ? `— ${d.quantity}` : ''}
                        </div>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {(file.receipts || []).length > 0 && (
        <>
          <h3 className="mt-3 font-bold">الإيصالات</h3>
          <table>
            <tbody>
              {file.receipts.map((r) => (
                <tr key={r.id}>
                  <th style={{ width: '22%' }}>
                    <span dir="ltr">{r.number}</span>
                    <div className="muted text-[10px]">{formatDate(r.issuedAt)}</div>
                  </th>
                  <td>
                    {(r.items || []).map((line) => line.label).join('، ')}
                    {r.total !== undefined ? ` — ${formatMoney(r.total)} (المدفوع ${formatMoney(r.paidAmount ?? 0)})` : ''}
                    {r.status === 'void' ? ' — ملغي' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {file.prescriptions.length > 0 && (
        <>
          <h3 className="mt-3 font-bold">الروشتات</h3>
          <table>
            <tbody>
              {file.prescriptions.map((rx) => (
                <tr key={rx.id}>
                  <th style={{ width: '22%' }}>
                    {rx.doctorName || ''}
                    <div className="muted text-[10px]">{formatDate(rx.date || rx.createdAt)}</div>
                  </th>
                  <td>
                    {rx.diagnosis && <div>التشخيص: {rx.diagnosis}</div>}
                    {(rx.medicines || []).map((m, i) => (
                      <div key={i}>
                        {m.name} {m.dosage || ''} {m.frequency || ''} {m.duration || ''}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
});

/** One labelled value on the identity card; digits kept in their own direction. */
function IdentityItem({ icon, label, value, ltrValue = false }) {
  return (
    <div className="flex min-w-0 items-start gap-2 rounded-xl bg-white/[0.03] px-3 py-2">
      <Icon name={icon} size={15} className="mt-0.5 shrink-0 text-cyan-300" />
      <div className="min-w-0">
        <p className="text-[11px] text-slate-400">{label}</p>
        <p className="break-words text-sm font-bold text-white" dir={ltrValue && value ? 'ltr' : undefined}>
          {value || '—'}
        </p>
      </div>
    </div>
  );
}

/**
 * The file as one story (QA report §1.2): every desk's entries merged and
 * dated, newest first, with chips to narrow it. See ./timeline.js.
 */
function FileTimeline({ file }) {
  const [filter, setFilter] = useState('all');
  const rows = buildTimeline(file);
  const shown = filterTimeline(rows, filter);
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="تصفية السجل">
        {TIMELINE_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            aria-pressed={filter === f.id}
            className={`chip-toggle ${filter === f.id ? 'chip-toggle--on' : ''}`}
          >
            {f.label}
            <span className="chip-toggle__count">{filterTimeline(rows, f.id).length}</span>
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <EmptyState icon="history" title="لا شيء هنا بعد" />
      ) : (
        <ol className="file-timeline">
          {shown.map((row, i) => {
            const kind = TIMELINE_KINDS[row.kind];
            return (
              <li key={row.key} className={`file-timeline__item tone-${kind.tone}`} style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}>
                <span className="file-timeline__dot" aria-hidden="true">
                  <Icon name={kind.icon} size={14} />
                </span>
                <div className="file-timeline__card">
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-400">
                    <span className="file-timeline__kind">{kind.label}</span>
                    <time dateTime={new Date(row.at).toISOString()}>{formatDateTime(row.at)}</time>
                  </p>
                  <p className="mt-1 break-words text-sm font-bold text-white">{row.title}</p>
                  {row.detail && <p className="mt-0.5 whitespace-pre-line break-words text-xs text-slate-300">{row.detail}</p>}
                  {row.chips.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {row.chips.map((chip) => (
                        <span key={chip} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-slate-300">
                          {chip}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

const RECEIPT_STATUS_META = {
  issued: { label: 'صادر', className: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
  collected: { label: 'محصّل', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' },
  void: { label: 'ملغي', className: 'border-slate-500/30 bg-slate-500/10 text-slate-400' },
};

/** The patient's receipts, as any desk may read them — amounts only if the server sent them. */
function FileReceipts({ receipts }) {
  if (!receipts.length) {
    return <EmptyState icon="receipt" title="لا توجد إيصالات" message="تظهر هنا إيصالات الاستقبال وما شملته من خدمات." />;
  }
  return (
    <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      {receipts.map((r) => (
        <li key={r.id} className={`glass-panel rounded-2xl border border-white/5 p-3 ${r.status === 'void' ? 'opacity-60' : ''}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Icon name="receipt" size={16} className="text-emerald-300" />
            <span className="font-mono text-sm font-bold text-white" dir="ltr">
              {r.number}
            </span>
            <Badge className={RECEIPT_STATUS_META[r.status]?.className}>{RECEIPT_STATUS_META[r.status]?.label || r.status}</Badge>
            <Badge className="border-white/10 bg-white/5 text-slate-300">{r.receiptType === 'deferred' ? 'آجل' : 'نقدي'}</Badge>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            {formatDateTime(r.issuedAt)}
            {r.signatureName ? ` · ${r.signatureName}` : ''}
            {r.signatureTitle ? ` (${r.signatureTitle})` : ''}
          </p>
          {r.serial && (
            <p className="mt-1 text-[11px] text-slate-500">
              السيريال <span className="font-mono text-slate-300" dir="ltr">{r.serial}</span>
            </p>
          )}
          <ul className="mt-2 space-y-0.5 text-sm text-slate-200">
            {(r.items || []).map((line, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span>
                  • {line.label}
                  {line.department ? <span className="text-xs text-cyan-300"> — {DEPARTMENT_META[line.department]?.label}</span> : null}
                </span>
                {line.amount !== undefined && <span className="shrink-0 whitespace-nowrap text-xs text-slate-400">{formatMoney(line.amount)}</span>}
              </li>
            ))}
          </ul>
          {r.total !== undefined && (
            <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
              <Badge className="border-white/10 bg-white/5 text-slate-200">الإجمالي {formatMoney(r.total)}</Badge>
              <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200">المدفوع {formatMoney(r.paidAmount ?? 0)}</Badge>
              {Number(r.remaining) > 0 && (
                <Badge className="border-amber-500/40 bg-amber-500/15 text-amber-100">المتبقي {formatMoney(r.remaining)}</Badge>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export function MedicalFileView({ patientId, bookingId = null, onEdit }) {
  const { can, institutionName } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('summary');
  const [printing, setPrinting] = useState(false);
  const [noteKind, setNoteKind] = useState('note');
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const sheet = useRef(null);

  const canAppend = can(PERMISSIONS.PATIENT_RECORD_APPEND);
  const canDiagnose = can(PERMISSIONS.PRESCRIPTION_WRITE);

  const load = async () => {
    setError(null);
    try {
      setFile(await patientsApi.file(patientId));
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await patientsApi.addNote(patientId, { kind: noteKind, text: noteText.trim(), bookingId });
      setNoteText('');
      success('تمت الإضافة إلى الملف');
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!file) return <Spinner label="جارٍ تحميل الملف الطبي" variant="inline" />;

  const p = file.patient;
  const age = ageFrom(p.dateOfBirth);
  const results = file.requests.filter((r) => r.status === 'completed');
  const open = file.requests.filter((r) => !['completed', 'cancelled', 'rejected'].includes(r.status));

  const receipts = file.receipts || [];
  const tabs = [
    { id: 'summary', label: 'الملخص', icon: 'file-heart' },
    { id: 'timeline', label: 'السجل الموحد', icon: 'history' },
    { id: 'notes', label: 'التشخيصات', icon: 'notebook-pen', count: file.notes.length },
    { id: 'results', label: 'النتائج', icon: 'file-check-2', count: results.length },
    { id: 'requests', label: 'طلبات جارية', icon: 'list-todo', count: open.length },
    { id: 'visits', label: 'الزيارات والروشتات', icon: 'calendar-days', count: file.visits.length },
    { id: 'nursing', label: 'التمريض والإقامة', icon: 'bed-double', count: file.nursing.length + file.beds.length },
    { id: 'attachments', label: 'المرفقات', icon: 'paperclip', count: file.attachments.length },
    { id: 'receipts', label: 'الإيصالات', icon: 'receipt', count: receipts.length },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className={`tile3d__face ${p.patientType === 'insured' ? 'tone-purple' : 'tone-emerald'} shrink-0`} aria-hidden="true">
          <Icon name={p.patientType === 'insured' ? 'shield-plus' : 'user'} size={24} className="tile3d__icon" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="break-words text-lg font-bold text-white">{p.fullName}</h3>
          <p className="text-xs text-slate-400">
            {age !== null ? `${age} سنة` : 'السن غير مسجل'}
            {p.dateOfBirth ? ` (مواليد ${ltr(formatDmy(p.dateOfBirth))})` : ''}
            {p.gender ? ` · ${GENDER[p.gender]}` : ''}
            {p.patientType === 'insured' ? ` · تأمين ${p.insuranceProvider || ''}` : ' · نقدي'}
            {p.insuranceExpiry ? ` · ينتهي ${formatDate(p.insuranceExpiry)}` : ''}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-200" icon="hash">
              ملف رقم <span dir="ltr">{p.id}</span>
            </Badge>
            {file.contractParty && (
              <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-200" icon="building-2">
                {file.contractParty.name}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {onEdit && (
            <Button variant="subtle" icon="pencil" onClick={onEdit}>
              تعديل البيانات
            </Button>
          )}
          <Button variant="ghost" icon="printer" onClick={() => setPrinting(true)}>
            طباعة الملف
          </Button>
        </div>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'summary' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="lg:col-span-2">
            <Section title="بيانات التسجيل — كما سجّلها الاستقبال" icon="id-card">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                <IdentityItem icon="hash" label="الرقم القومي" value={p.nationalId} ltrValue />
                <IdentityItem icon="phone" label="الهاتف" value={[p.phonePrimary, p.phoneSecondary].filter(Boolean).join(' / ')} ltrValue />
                <IdentityItem icon="calendar-days" label="تاريخ الميلاد" value={p.dateOfBirth ? formatDmy(p.dateOfBirth) : null} ltrValue />
                <IdentityItem icon="building-2" label="جهة التعاقد" value={file.contractParty?.name || p.insuranceProvider} />
                <IdentityItem icon="shield-plus" label="كود التأمين" value={p.insuranceCode} ltrValue />
                <IdentityItem icon="briefcase" label="جهة العمل" value={p.employerName} />
              </div>
            </Section>
          </div>
          <Section title="الأمراض المزمنة" icon="heart-pulse">
            {(p.chronicDiseases || []).length ? (
              <div className="flex flex-wrap gap-1.5">
                {p.chronicDiseases.map((d) => (
                  <Badge key={d} className="border-rose-500/30 bg-rose-500/10 text-rose-200">
                    {d}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">لا يوجد</p>
            )}
          </Section>
          <Section title="الأدوية الحالية" icon="pill">
            {(p.currentMedications || []).length ? (
              <ul className="space-y-1 text-sm text-slate-200">
                {p.currentMedications.map((m, i) => (
                  <li key={i}>
                    <b>{m.name}</b> {m.concentration && <span className="text-slate-400">{m.concentration}</span>}
                    {m.frequency && <span className="text-slate-400"> · {m.frequency}</span>}
                    {m.duration && <span className="text-slate-400"> · {m.duration}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">لا يوجد</p>
            )}
          </Section>
          <Section title="الشكوى" icon="message-square-text">
            <p className="whitespace-pre-line text-sm text-slate-200">{p.complaints || '—'}</p>
          </Section>
          <Section title="التاريخ المرضي" icon="history">
            <p className="whitespace-pre-line text-sm text-slate-200">{p.medicalHistory || '—'}</p>
          </Section>
          {file.notes.find((n) => n.kind === 'ai_summary') && (
            <Section title="آخر ملخص بالذكاء الاصطناعي" icon="sparkles">
              <p className="whitespace-pre-line text-sm text-purple-100">{file.notes.find((n) => n.kind === 'ai_summary').text}</p>
              <p className="mt-2 text-[11px] text-slate-500">ملخص مساعد للطبيب وليس تشخيصاً.</p>
            </Section>
          )}
        </div>
      )}

      {tab === 'timeline' && <FileTimeline file={file} />}

      {tab === 'receipts' && <FileReceipts receipts={receipts} />}

      {tab === 'notes' && (
        <div className="space-y-3">
          {canAppend && (
            <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.05] p-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[12rem_1fr_auto]">
                <Field label="النوع">
                  <Select value={noteKind} onChange={(e) => setNoteKind(e.target.value)}>
                    <option value="note">ملاحظة</option>
                    <option value="complaint">شكوى</option>
                    {canDiagnose && <option value="initial_diagnosis">تشخيص مبدئي</option>}
                    {canDiagnose && <option value="final_diagnosis">تشخيص نهائي</option>}
                  </Select>
                </Field>
                <Field label="النص">
                  <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
                </Field>
                <div className="flex items-end">
                  <Button icon="plus" loading={saving} onClick={addNote}>
                    إضافة
                  </Button>
                </div>
              </div>
            </div>
          )}
          {file.notes.length === 0 ? (
            <EmptyState icon="notebook-pen" title="لا توجد تشخيصات أو ملاحظات بعد" />
          ) : (
            file.notes.map((n) => (
              <div key={n.id} className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <Badge className={NOTE_KIND_META[n.kind]?.className}>{NOTE_KIND_META[n.kind]?.label || n.kind}</Badge>
                  {n.createdByName && <span>{n.createdByName}</span>}
                  <span>{formatDateTime(n.createdAt)}</span>
                </div>
                <p className="whitespace-pre-line text-sm text-slate-100">{n.text}</p>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'results' && (
        <div className="space-y-3">
          {results.length === 0 ? (
            <EmptyState icon="file-check-2" title="لا توجد نتائج بعد" message="تظهر نتائج المعمل والأشعة والصيدلية هنا فور اعتمادها." />
          ) : (
            results.map((r) => (
              <div key={r.id} className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.04] p-3">
                <p className="mb-2 flex flex-wrap items-center gap-2 text-sm font-bold text-white">
                  <Icon name={DEPARTMENT_META[r.department]?.icon || 'file'} size={15} className="text-emerald-300" />
                  {DEPARTMENT_META[r.department]?.label} — {r.title}
                  <span className="text-xs font-normal text-slate-400">{formatDateTime(r.completedAt)}</span>
                </p>
                <RequestResult request={r} />
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'requests' && (
        <div className="space-y-3">
          {open.length === 0 ? (
            <EmptyState icon="list-todo" title="لا توجد طلبات جارية" />
          ) : (
            open.map((r) => <RequestCard key={r.id} request={r} showPatient={false} showMoney={Boolean(r.quote)} />)
          )}
        </div>
      )}

      {tab === 'visits' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section title="الزيارات" icon="calendar-days">
            {file.visits.length === 0 ? (
              <p className="text-sm text-slate-500">لا توجد زيارات مسجلة</p>
            ) : (
              <ul className="space-y-1 text-sm text-slate-200">
                {file.visits.map((v) => (
                  <li key={v.id} className="rounded-lg bg-white/[0.03] p-2">
                    <div className="flex flex-wrap justify-between gap-2">
                      <span>
                        {v.date ? ltr(formatDmy(v.date)) : ''} {v.time ? ltr(v.time) : ''} · {v.doctorName || ''}{' '}
                        {v.clinicName ? `· ${v.clinicName}` : ''}
                      </span>
                      <span className="text-xs text-slate-400">{v.status}</span>
                    </div>
                    {v.intake?.chiefComplaint && <p className="mt-1 text-xs text-amber-200">الشكوى: {v.intake.chiefComplaint}</p>}
                    {(v.intake?.customFields || []).length > 0 && (
                      <ul className="mt-1 flex flex-wrap gap-1">
                        {v.intake.customFields.map((f) => (
                          <li key={f.key} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-slate-300">
                            {f.label}: {f.value === true ? 'نعم' : f.value === false ? 'لا' : String(f.value)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="الروشتات" icon="scroll-text">
            {file.prescriptions.length === 0 ? (
              <p className="text-sm text-slate-500">لا توجد روشتات</p>
            ) : (
              <ul className="space-y-2 text-sm text-slate-200">
                {file.prescriptions.map((rx) => (
                  <li key={rx.id} className="rounded-lg bg-white/[0.03] p-2">
                    <p className="text-xs text-slate-400">
                      {formatDate(rx.date || rx.createdAt)} · {rx.doctorName || ''}
                    </p>
                    {rx.diagnosis && <p className="text-slate-100">{rx.diagnosis}</p>}
                    {(rx.medicines || []).map((m, i) => (
                      <p key={i} className="text-xs">
                        • {m.name} {m.dosage || ''} {m.frequency || ''} {m.duration || ''}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}

      {tab === 'nursing' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section title="الإقامة بالأسرّة" icon="bed-double">
            {file.beds.length === 0 ? (
              <p className="text-sm text-slate-500">لم يُحجز سرير</p>
            ) : (
              <ul className="space-y-1 text-sm text-slate-200">
                {file.beds.map((b) => (
                  <li key={b.id} className="flex flex-wrap justify-between gap-2">
                    <span>
                      سرير #{b.bedId} · {formatDateTime(b.startAt)}
                      {b.endedAt ? ` ← ${formatDateTime(b.endedAt)}` : ''}
                    </span>
                    <span className={`rounded px-1.5 text-xs ${ASSIGNMENT_META[b.status]?.className || ''}`}>{ASSIGNMENT_META[b.status]?.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="سجل التمريض" icon="activity">
            {file.nursing.length === 0 ? (
              <p className="text-sm text-slate-500">لا توجد تسجيلات</p>
            ) : (
              <ul className="space-y-1.5 text-sm text-slate-200">
                {file.nursing.map((n) => (
                  <li key={n.id} className="rounded-lg bg-white/[0.03] p-2">
                    <p className="text-[11px] text-slate-400">
                      {formatDateTime(n.recordedAt)} {n.recordedByName ? `· ${n.recordedByName}` : ''}
                    </p>
                    {n.kind === 'vitals' && <p>{vitalsText(n.payload)}</p>}
                    {n.kind === 'medication' && (
                      <p>
                        💊 {n.payload?.name} {n.payload?.dose || ''} {n.payload?.route || ''}
                      </p>
                    )}
                    {n.text && <p className="whitespace-pre-line">{n.text}</p>}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}

      {tab === 'attachments' && <AttachmentGrid rows={file.attachments} />}

      <Modal
        open={printing}
        onClose={() => setPrinting(false)}
        title="طباعة الملف الطبي"
        icon="printer"
        size="xl"
        footer={
          <div className="flex justify-end">
            <Button icon="printer" onClick={() => printArea(sheet.current, { title: file?.patient?.fullName || 'الملف الطبي' })}>
              طباعة / حفظ PDF
            </Button>
          </div>
        }
      >
        <MedicalFileSheet ref={sheet} file={file} institutionName={institutionName} />
      </Modal>
    </div>
  );
}

export default MedicalFileView;
